import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, DollarSign, CheckCircle, Clock, User, AlertCircle, TrendingUp, Download, Eye, Info } from 'lucide-react';
import { payoutsService, enhancedAdminService } from '../services/api';
import { format } from 'date-fns';

const PayoutStatusBadge = ({ status }) => {
  const statusConfig = {
    completed: { color: 'bg-green-100 text-green-800 border border-green-200', icon: CheckCircle },
    pending: { color: 'bg-yellow-100 text-yellow-800 border border-yellow-200', icon: Clock },
    processing: { color: 'bg-[#1d1d1d] text-white border border-gray-300', icon: Clock },
    failed: { color: 'bg-red-100 text-red-800 border border-red-200', icon: AlertCircle }
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${config.color}`}>
      <Icon className="h-3 w-3 mr-1.5" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const AuthorEarningsModal = ({ author, isOpen, onClose }) => {
  const { data: earningsDetail, isLoading } = useQuery({
    queryKey: ['author-earnings', author?.author_id],
    queryFn: () => enhancedAdminService.getAuthorEarningsDetail(author.author_id),
    enabled: !!author && isOpen
  });

  if (!isOpen || !author) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Earnings Details</h2>
            <p className="text-gray-600 text-sm">{author.author_name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <AlertCircle className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1d1d1d]"></div>
            </div>
          ) : earningsDetail ? (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <p className="text-sm font-medium text-green-800 mb-1">Total Earnings</p>
                  <p className="text-2xl font-bold text-green-900">
                    GHS {earningsDetail.summary?.total_earnings?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-sm font-medium text-blue-800 mb-1">Paid Out</p>
                  <p className="text-2xl font-bold text-blue-900">
                    GHS {earningsDetail.summary?.paid_out?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <p className="text-sm font-medium text-yellow-800 mb-1">Pending Payout</p>
                  <p className="text-2xl font-bold text-yellow-900">
                    GHS {earningsDetail.summary?.pending_payout?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>

              {/* Recent Earnings */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Earnings</h3>
                <div className="space-y-3">
                  {earningsDetail.recent_earnings?.map((earning) => (
                    <div key={earning.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{earning.book_title}</p>
                        <p className="text-sm text-gray-600">
                          {format(new Date(earning.created_at), 'MMM dd, yyyy')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">
                          GHS {earning.author_share}
                        </p>
                        <p className="text-sm text-gray-600">
                          {earning.paid_out ? 'Paid' : 'Pending'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600">Unable to load earnings details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CustomPayoutModal = ({ author, isOpen, onClose, onPayout }) => {
  const [amount, setAmount] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) return;

    setProcessing(true);
    try {
      await onPayout(author.author_id, parseFloat(amount));
      setAmount('');
      onClose();
    } catch (error) {
      console.error('Payout error:', error);
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen || !author) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Process Payout</h2>
          <p className="text-gray-600 text-sm">{author.author_name}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount (GHS)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              max={author.pending_amount}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d]"
              placeholder={`Max: GHS ${author.pending_amount}`}
            />
            <p className="text-sm text-gray-500 mt-1">
              Maximum available: GHS {author.pending_amount}
            </p>
          </div>

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!amount || processing}
              className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {processing ? 'Processing...' : 'Process Payout'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function EnhancedPayouts() {
  const [filters, setFilters] = useState({
    search: '',
    status: ''
  });
  const [processingPayout, setProcessingPayout] = useState(null);
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [showEarningsModal, setShowEarningsModal] = useState(false);
  const [showCustomPayoutModal, setShowCustomPayoutModal] = useState(false);
  const queryClient = useQueryClient();

  const { data: pendingPayouts, isLoading: pendingLoading, error: pendingError } = useQuery({
    queryKey: ['pending-payouts'],
    queryFn: () => enhancedAdminService.getPendingAllPayouts?.() || payoutsService.getPending(),
    refetchInterval: 30000,
  });

  const { data: payoutHistory, isLoading: historyLoading, error: historyError } = useQuery({
    queryKey: ['payout-history', filters],
    queryFn: () => payoutsService.getPayoutHistory(filters),
    refetchInterval: 30000,
  });

  const processPayoutMutation = useMutation({
    mutationFn: ({ authorId, amount }) => payoutsService.processPayout(authorId, amount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-payouts'] });
      queryClient.invalidateQueries({ queryKey: ['payout-history'] });
      setProcessingPayout(null);
      alert('Payout processed successfully! Remember to complete the MoMo transfer and mark it as completed.');
    },
    onError: (error) => {
      console.error('Payout processing error:', error);
      alert('Error processing payout: ' + (error.response?.data?.error || error.message || 'Unknown error'));
      setProcessingPayout(null);
    }
  });

  const markCompletedMutation = useMutation({
    mutationFn: ({ payoutId, momoTransactionId }) =>
      enhancedAdminService.markPayoutCompleted(payoutId, momoTransactionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-payouts'] });
      queryClient.invalidateQueries({ queryKey: ['payout-history'] });
      alert('Payout marked as completed.');
    },
    onError: (error) => {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  });

  const customPayoutMutation = useMutation({
    mutationFn: ({ authorId, amount }) => enhancedAdminService.processPayout(authorId, amount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-payouts'] });
      queryClient.invalidateQueries({ queryKey: ['payout-history'] });
      setShowCustomPayoutModal(false);
    },
    onError: (error) => {
      console.error('Custom payout error:', error);
      alert('Error processing payout: ' + (error.response?.data?.error || 'Unknown error'));
    }
  });

  const handleProcessPayout = (authorId, authorName, amount) => {
    const confirmed = window.confirm(
      `Process payout of GHS ${parseFloat(amount || 0).toFixed(2)} to ${authorName}?\n\nIMPORTANT: You still need to manually send the MoMo transfer and then mark it as completed.`
    );
    if (confirmed) {
      setProcessingPayout(authorId);
      processPayoutMutation.mutate({ authorId, amount: parseFloat(amount || 0) });
    }
  };

  const handleMarkCompleted = (payoutId) => {
    const momoId = window.prompt('Enter MoMo Transaction ID (optional):') || '';
    markCompletedMutation.mutate({ payoutId, momoTransactionId: momoId });
  };

  const handleCustomPayout = (authorId, amount) => {
    customPayoutMutation.mutate({ authorId, amount });
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const totalPendingAmount = pendingPayouts?.reduce((sum, payout) => sum + parseFloat(payout.pending_amount || 0), 0) || 0;

  if (pendingError || historyError) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <AlertCircle className="mx-auto h-16 w-16 text-red-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error loading payouts</h3>
          <p className="text-gray-600 font-medium">Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Payout Management</h1>
          <p className="text-gray-600 font-medium">Manage author payouts and payment processing</p>
        </div>
        <button className="bg-[#1d1d1d] text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-800 transition-all duration-200 shadow-md hover:shadow-lg flex items-center">
          <Download className="h-4 w-4 mr-2" />
          Export Report
        </button>
      </div>

      <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-yellow-100 rounded-xl">
              <TrendingUp className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-yellow-800">Pending Payouts Summary</h3>
              <p className="text-yellow-700 font-medium">
                Total amount to be paid out: <strong className="text-xl">GHS {totalPendingAmount.toFixed(2)}</strong>
              </p>
            </div>
          </div>
          <div className="text-2xl font-bold text-yellow-800 bg-yellow-100 px-4 py-2 rounded-xl">
            {pendingPayouts?.length || 0} Authors
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-xs border border-gray-200">
        <div className="px-6 py-5 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Pending Payouts</h2>
        </div>

        {pendingLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1d1d1d]"></div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {pendingPayouts?.map((payout) => (
              <div key={payout.author_id} className="px-6 py-5 hover:bg-gray-50 transition-colors duration-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {payout.author_logo ? (
                      <img
                        src={payout.author_logo}
                        alt={payout.author_name}
                        className="h-14 w-14 rounded-xl object-cover border border-gray-200"
                      />
                    ) : (
                      <div className="h-14 w-14 bg-gray-200 rounded-xl flex items-center justify-center border border-gray-300">
                        <User className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{payout.author_name}</h3>
                      <p className="text-sm text-gray-600 font-medium mb-2">
                        {payout.momo_number || 'No MoMo number provided'}
                      </p>
                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">
                          {payout.pending_sales} pending sales
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-6">
                    <div className="text-right">
                      <p className="text-xl font-bold text-gray-900">
                        GHS {parseFloat(payout.pending_amount || 0).toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-600 font-medium">
                        Platform: GHS {parseFloat(payout.platform_share || 0).toFixed(2)}
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => {
                          setSelectedAuthor(payout);
                          setShowEarningsModal(true);
                        }}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-semibold rounded-xl text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Details
                      </button>
                      
                      <button
                        onClick={() => {
                          setSelectedAuthor(payout);
                          setShowCustomPayoutModal(true);
                        }}
                        className="inline-flex items-center px-4 py-2 border border-blue-300 text-sm font-semibold rounded-xl text-blue-700 bg-blue-50 hover:bg-blue-100 transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        <DollarSign className="h-4 w-4 mr-2" />
                        Custom
                      </button>
                      
                      <button
                        onClick={() => handleProcessPayout(payout.author_id, payout.author_name)}
                        disabled={processingPayout === payout.author_id}
                        className="inline-flex items-center px-5 py-2 border border-transparent text-sm font-semibold rounded-xl text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"
                      >
                        {processingPayout === payout.author_id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                          <CheckCircle className="h-4 w-4 mr-2" />
                        )}
                        Full Payout
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {(!pendingPayouts || pendingPayouts.length === 0) && !pendingLoading && (
          <div className="text-center py-16">
            <CheckCircle className="mx-auto h-16 w-16 text-green-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No pending payouts</h3>
            <p className="text-gray-600 font-medium">All author earnings have been paid out.</p>
          </div>
        )}
      </div>

      {/* Rest of the payout history table remains the same */}

      <AuthorEarningsModal
        author={selectedAuthor}
        isOpen={showEarningsModal}
        onClose={() => setShowEarningsModal(false)}
      />

      <CustomPayoutModal
        author={selectedAuthor}
        isOpen={showCustomPayoutModal}
        onClose={() => setShowCustomPayoutModal(false)}
        onPayout={handleCustomPayout}
      />
    </div>
  );
}